import {
  creditRatingPortfoliosToTableParser,
  creditReviewPortfoliosToTableParser,
  creditReviewToTableParser,
  dossierItemsToTableParser,
  esgPortfoliosToTableParser,
  esgSentimentToTableParser,
} from 'shared/parsers';
import produce from 'immer';

import { ratingTypes } from 'shared/constants/ratingTypes';
import {
  GET_DOSSIERS,
  GET_DOSSIERS_SUCCESS,
  GET_DOSSIERS_ERROR,
  CREATE_DOSSIER,
  CREATE_DOSSIER_SUCCESS,
  CREATE_DOSSIER_ERROR,
  DELETE_DOSSIER,
  DELETE_DOSSIER_SUCCESS,
  DELETE_DOSSIER_ERROR,
  REFRESH_DOSSIERS,
  GET_DOSSIER,
  GET_DOSSIER_SUCCESS,
  GET_DOSSIER_ERROR,
  GET_DOSSIER_ITEMS,
  GET_DOSSIER_ITEMS_SUCCESS,
  GET_DOSSIER_ITEMS_ERROR,
  TOGGLE_DOSSIER_WATCHED_STATE,
  TOGGLE_DOSSIER_WATCHED_STATE_FINISHED,
} from './dossierActions';

import { REQUEST_SIG_ENTITY_RATING_SUCCESS } from '../entity/entityActions';

const dossiersByRatingType = Object.values(ratingTypes).reduce((acc, ratingType) => {
  acc[ratingType] = {
    isPending: false,
    shouldRefresh: { value: false, reason: null },
    total: 0,
    data: [],
  };

  return acc;
}, {});

const initialState = {
  isPending: false,
  isModalPending: false,
  shouldRefresh: { value: false, reason: null },
  singleDossier: null,
  error: null,
  byRatingType: dossiersByRatingType,
  items: {
    issuers: {
      isPending: false,
      total: 0,
      data: [],
    },
    issues: {
      isPending: false,
      total: 0,
      data: [],
    },
    researches: {
      isPending: false,
      total: 0,
      data: [],
    },
    commentaries: {
      isPending: false,
      total: 0,
      data: [],
    },
    ratingNews: {
      isPending: false,
      total: 0,
      data: [],
    },
    companyNews: {
      isPending: false,
      total: 0,
      data: [],
    },
  },
};

const parserByRatingType = {
  [ratingTypes.CREDIT_RATING]: creditRatingPortfoliosToTableParser,
  [ratingTypes.CREDIT_REVIEW]: creditReviewPortfoliosToTableParser,
  [ratingTypes.ESG_IMPACT]: esgPortfoliosToTableParser,
  [ratingTypes.SENTIMENT]: esgSentimentToTableParser,
};

export function dossier(state = initialState, action) {
  const { payload, type } = action;

  switch (type) {
    case REFRESH_DOSSIERS: {
      const { ratingType, reason } = payload;

      return produce(state, (draftState) => {
        const dossiers = state.byRatingType[ratingType];
        draftState.error = null; // eslint-disable-line no-param-reassign
        // eslint-disable-next-line no-param-reassign
        draftState.byRatingType[ratingType] = {
          ...dossiers,
          isPending: true,
          shouldRefresh: { value: true, reason },
        };
      });
    }
    case GET_DOSSIER:
      return { ...state, isPending: true, items: initialState.items, error: null };
    case GET_DOSSIER_SUCCESS:
      return { ...state, isPending: false, singleDossier: payload.data };
    case GET_DOSSIER_ERROR:
      return { ...state, isPending: false };
    case GET_DOSSIER_ITEMS: {
      const { itemType } = payload;

      return {
        ...state,
        error: null,
        items: {
          ...state.items,
          [itemType]: {
            ...state.items[itemType],
            isPending: true,
          },
        },
      };
    }
    case GET_DOSSIER_ITEMS_SUCCESS: {
      const { itemType, ratingType } = payload;
      return {
        ...state,
        items: {
          ...state.items,
          [itemType]: {
            ...state.items[itemType],
            isPending: false,
            data: dossierItemsToTableParser(payload.data, itemType, ratingType),
            total: payload.total,
          },
        },
      };
    }
    case GET_DOSSIER_ITEMS_ERROR:
      return { ...state, items: initialState.items };
    case GET_DOSSIERS: {
      const { ratingType } = payload;

      return produce(state, (draftState) => {
        const dossiers = state.byRatingType[ratingType];
        draftState.error = null; // eslint-disable-line no-param-reassign
        // eslint-disable-next-line no-param-reassign
        draftState.byRatingType[ratingType] = {
          ...dossiers,
          isPending: true,
        };
      });
    }
    case GET_DOSSIERS_SUCCESS: {
      const { response, ratingType } = payload;

      return produce(state, (draftState) => {
        const dossiers = state.byRatingType[ratingType];
        const dossiersParser = parserByRatingType[ratingType];
        // eslint-disable-next-line no-param-reassign
        draftState.byRatingType[ratingType] = {
          ...dossiers,
          isPending: false,
          shouldRefresh: initialState.shouldRefresh,
          data: dossiersParser(response.data),
          total: response.total,
        };
      });
    }
    case GET_DOSSIERS_ERROR: {
      const { ratingType } = payload;

      return produce(state, (draftState) => {
        const dossiers = state.byRatingType[ratingType];
        // eslint-disable-next-line no-param-reassign
        draftState.byRatingType[ratingType] = {
          ...dossiers,
          isPending: false,
          shouldRefresh: initialState.shouldRefresh,
        };
      });
    }
    case CREATE_DOSSIER:
      return { ...state, isModalPending: true, error: null };
    case CREATE_DOSSIER_SUCCESS:
      return {
        ...state,
        isModalPending: false,
      };
    case CREATE_DOSSIER_ERROR:
      return {
        ...state,
        isModalPending: false,
        error: { type: payload.errorType, message: payload.errorMessage },
      };
    case DELETE_DOSSIER:
      return { ...state, isPending: true, error: null };
    case DELETE_DOSSIER_SUCCESS:
      return { ...state, isPending: false };
    case DELETE_DOSSIER_ERROR:
      return { ...state, isPending: false };
    case TOGGLE_DOSSIER_WATCHED_STATE:
      return { ...state, isPending: true };
    case TOGGLE_DOSSIER_WATCHED_STATE_FINISHED:
      return { ...state, isPending: false };
    case REQUEST_SIG_ENTITY_RATING_SUCCESS: {
      const updatedItems = payload;

      return produce(state, (draftState) => {
        // Toggle flag (last value in array) which is responsible for showing the rating for particular entity row
        // eslint-disable-next-line no-param-reassign
        draftState.items.issuers.data = state.items.issuers.data.map((item) => {
          const easyNumber = item[0];
          const updatedItem = updatedItems.find((company) => company.easyNumber === easyNumber);

          if (updatedItem) return creditReviewToTableParser(updatedItem);

          return item;
        });
      });
    }
    default:
      return state;
  }
}
